import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
} from '@dnd-kit/sortable';
import { Backdrop, CircularProgress, Grid, Typography } from '@mui/material';
import { connect } from 'react-redux';
import Column from './Column';
import { SortableItem } from './SortableItem';
import { getallNotisForAufgabe, assignNotifications } from '../../actions/notification';
import { useScreenSize } from '../../components/Atoms/ScreenSizeHook';

export default connect(
  ({
    data,
    subscribedEquipmentpools,
    subscribedEquipments,
    subscribedAreas,
    subscribedSites,
    users,
    aufgabeNotifications
  }) => ({
    data,
    subscribedEquipmentpools,
    subscribedEquipments,
    subscribedAreas,
    subscribedSites,
    users,
    aufgabeNotifications
  }),
  {
    getallNotisForAufgabe,
    assignNotifications
  }
)((props) => {


  const { windowWidth, breakpoint } = useScreenSize();

  const { notificationTypesFilter, userFilter, areaPoolFilter, showEquipment, isFocused } = props;

  const [backendOutputObj, setbackendOutputObj] = useState({});

  const [columns, setColumns] = useState({
    unassignedNotification: [],
  });

  const [activeId, setActiveId] = useState(null);
  const [expanded, setExpanded] = useState({});         // handeling collapse and expand

  const [refresh, setRefresh] = useState(false);     // for board refresh

  const [taskMoving, setTaskMoving] = useState(false);

  const activeColumnRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5
    }
  }));    // builtin by library


  // finding column (board)

  const findContainer = (id) => {
    return Object.keys(columns).find((key) => columns[key].find((key) => { return key.nodeid === id }));
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const colId = findContainer(active.id);
    activeColumnRef.current = colId ?? null;
    setActiveId(active.id);
  };

  const makePayloadForBackend = (source, destination, notificationnodeid, parentnodeid) => {

    let _tempbackendObj = {};

    if (destination === "unassignedNotification") {
      //in case of move notificaiton from user box to unassigned box

      _tempbackendObj = {
        "usernodeid": source,
        "unassignednotification": notificationnodeid
      }
    }

    else {
      _tempbackendObj = {
        "usernodeid": destination,
        "assignednotification": {
          "notificationnodeid": notificationnodeid,
          "parentnodeid": parentnodeid
        }
      }
    }

    setbackendOutputObj(_tempbackendObj);
  }


  const handleDragEnd = (event) => {

    // study dndnext kit docs for better overview

    let _tempColmns = {};

    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const sourceCol = findContainer(active.id);
    const destCol = columns[over.id] ? over.id : findContainer(over.id);

    if (!sourceCol || !destCol) return;

    const sourceItems = [...columns[sourceCol]];
    const destItems = [...columns[destCol]];

    let parentNodeId = "";

    const activeIndex = sourceItems.findIndex((item) => item.nodeid === active.id);
    const draggedItem = sourceItems[activeIndex];

    const overIndex = destItems.findIndex((item) => item.nodeid === over.id);

    if (sourceCol === destCol) {

      // drag and drop in case column (board)

      const newItems = arrayMove(sourceItems, activeIndex, overIndex);
      _tempColmns = { ...columns, [sourceCol]: newItems }
      setColumns(_tempColmns);
    } else {

      parentNodeId = sourceCol === "unassignedNotification" ? "" : sourceCol;

      // console.log('Moved from:', sourceCol, 'to:', destCol, "item : ", active.id);

      sourceItems.splice(activeIndex, 1);

      const updatedDraggedItem = { ...draggedItem, parentNodeid: parentNodeId };

      destItems.splice(overIndex >= 0 ? overIndex : destItems.length, 0, updatedDraggedItem);

      _tempColmns = {
        ...columns,
        [sourceCol]: sourceItems,
        [destCol]: destItems,
      }

      makePayloadForBackend(sourceCol, destCol, active.id, parentNodeId);
      setColumns(_tempColmns);
    }
  };

  // expand or collapse view
  const toggleAccordion = (colId, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [colId]: isExpanded }));
  };


  // find notification for overlay ( you see when you drag start (shadow))

  let targetNotification = undefined;

  Object.keys(columns).find((key) => columns[key].find((key) => {
    if (key.nodeid === activeId) {
      targetNotification = key
    }
  }));

  // appending users in initialData

  const transformNotifications = (inputJson, userList) => {
    // Step 1: Initialize result object with all users
    const result = {
      unassignedNotification: {},
    };

    userList.length && userList.forEach((usr) => {
      const userNodeId = usr?.user?.nodeid;
      if (userNodeId) {
        result[userNodeId] = [];
      }
    });

    result.unassignedNotification = [];

    // Step 2: Process equipment data
    const data = inputJson?.[0]?.data || [];

    data.forEach((item) => {
      const equipment = item.equipment;
      const equipmentNodeId = equipment?.nodeid;

      if (equipment.hasnotification) {
        equipment.hasnotification.forEach((n) => {
          const notification = { ...n.notification, equipmentnodeid: equipmentNodeId };

          // If notification has assigned users
          if (notification.isassignedto && Array.isArray(notification.isassignedto)) {
            let assigned = false;
            notification.isassignedto.forEach((userWrap) => {
              const userId = userWrap.user?.nodeid;
              if (userId && result.hasOwnProperty(userId)) {
                result[userId].push(notification);
                assigned = true;
              }
            });

            // Fallback if assigned to unknown user
            if (!assigned) {
              result.unassignedNotification.push(notification);
            }
          } else {
            // No assignment at all
            result.unassignedNotification.push(notification);
          }
        });
      }
    });

    return result;
  };

  const getAllNotis = () => {
    let nodeIds = [];
    for (const [key, value] of Object.entries(props.subscribedEquipments)) {
      nodeIds.push(value.nodeid);
    }
    props.getallNotisForAufgabe({ equipmentnodeids: nodeIds, lang: "de", status: "2" }, "AUFGABE_NOTIS")
  }

  // getting data for initial render
  useEffect(() => {
    getAllNotis();
  }, [isFocused]);


  // making data for UI state
  useEffect(() => {
    if (props.aufgabeNotifications?.length) {
      if (props.aufgabeNotifications[0].data.length) {
        const structuredData = transformNotifications(props.aufgabeNotifications, props.users);
        setColumns(structuredData); // or setState(structuredData)
      }
    }
  }, [props.aufgabeNotifications])


  // whenever if there is any assignment process ( onDropEnd ) , calls backend API
  useEffect(() => {
    if (Object.keys(backendOutputObj).length) {
      props.assignNotifications(backendOutputObj, "ASSIGN_NOTIFICATION");
    }
  }, [backendOutputObj]);



  const parseAreaPoolValue = (value) => {
    if (value) {
      const [type, nodeid] = value.split("==>");    // s==>some_nodeid ; s=site, a=area, p=equipment pool
      return { type, nodeid };
    }
    else {
      return { type: "", nodeid: "" };

    }
  };

  // this function is used to get Notification based on UI filters
  const filterTransformedNotifications = (data, selectedTypes, selectedUsers, areaPoolFilterValue, _dataLookup) => {

    const result = {};
    const { type: locationType, nodeid: locationId } = parseAreaPoolValue(areaPoolFilterValue);

    const matchesType = (notification) =>
      selectedTypes.includes('all') || selectedTypes.includes(notification.type);

    const matchesUser = (key) =>
      selectedUsers.includes('all') || selectedUsers.includes(key);
    const matchesLocation = (notification) => {
      if (!areaPoolFilterValue || areaPoolFilterValue === 'p==>0') return true;


      const equipmentId = notification.equipmentnodeid;
      const info = _dataLookup[equipmentId];



      if (!info) return false;

      if (locationType === 's') return info.site?.nodeid === locationId;
      if (locationType === 'a') return info.area?.nodeid === locationId;
      if (locationType === 'p') return info.equipmentpool?.nodeid === locationId;

      return false;
    };

    Object.entries(data).forEach(([key, notifications]) => {
      if (key === 'unassignedNotification' || matchesUser(key)) {
        result[key] = notifications
          .filter((n) => matchesType(n) && matchesLocation(n))
          .map((n) => ({ ...n }));
      }
    });

    return result;
  };


  // this function is used to check the relation of notification to equipment ( either its from area or pool)
  const buildDataLookup = () => {
    const data = props.aufgabeNotifications?.[0]?.data || [];

    // console.log("📦 rawJson.result[0].data length:", data?.length);
    if (!data || !Array.isArray(data)) {
      // console.error("❌ Invalid input passed to buildDataLookup");
      return {};
    }

    const lookup = {};

    data.forEach(({ equipment }) => {
      const equipmentId = equipment.nodeid;

      const info = {
        site: null,
        area: null,
        equipmentpool: null,
      };

      const links = Array.isArray(equipment.isequipmentof)
        ? equipment.isequipmentof
        : [equipment.isequipmentof];

      links.forEach((link) => {
        if (link.site) {
          info.site = link.site;
        }

        if (link.area) {
          info.area = link.area;
          const siteViaArea = link.area.isareaof?.[0]?.site;
          if (siteViaArea) {
            info.site = siteViaArea;
          }
        }

        if (link.equipmentpool) {
          info.equipmentpool = link.equipmentpool;
        }
      });

      lookup[equipmentId] = info;
    });

    return lookup;
  };


  const _dataLookup = buildDataLookup();

  const filteredData = useMemo(() => {
    setRefresh(!refresh);
    return filterTransformedNotifications(columns, notificationTypesFilter, userFilter, areaPoolFilter, _dataLookup);
  }, [columns, notificationTypesFilter, userFilter, areaPoolFilter]);


  // in case if user choose a notification from Placeholder Menu ( by clicking on zuweisen) OR from the Users List ( Notification Category Badge)
  const handleClickPromAssignment = (notificationNodeid, usernodeid, parentNodeId) => {
    setTaskMoving(true);
    let _tempbackendObj = {};
    _tempbackendObj = {
      "usernodeid": usernodeid,
      "assignednotification": {
        "notificationnodeid": notificationNodeid,
        "parentnodeid": parentNodeId // "" because from unassigned to any user... , 
      }
    }

    setbackendOutputObj(_tempbackendObj);

    // get latest data from backend
    setTimeout(() => {
      setTaskMoving(false);
      getAllNotis();
    }, 1500)
  }

  const handleUserClick = (destination, notificationnodeid, source) => {
    handleClickPromAssignment(notificationnodeid, destination, source);
  }

  let colSize = 12;

  if (windowWidth <= 3900 ) {
    colSize = isFocused ? 1.5 : 6; // lg

    if (windowWidth <= 3500 ) {
      colSize = isFocused ? 2 : 6; // lg
    }  

    if (windowWidth <= 1920 ) {
      colSize = isFocused ? 3 : 6; // lg
    } 

    if (windowWidth <= 1440 ) {
      colSize = isFocused ? 4 : 6; // lg
    } 
  }
  
  return (
    <>

      <Backdrop
        open={taskMoving}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backdropFilter: "blur(4px)",
          flexDirection: "column",
          opacity: 0
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }} color={"#FFFFFF"}>Bitte warten...</Typography>
      </Backdrop>

      {
        Object.entries(filteredData).length ?
          <div className='dnd_plannar_wrapper'>
            <DndContext
              sensors={sensors}
              collisionDetection={rectIntersection}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', borderRadius: "8px" }}>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                  <Column handleUserClick={handleUserClick} isFocused={isFocused} showEquipment={showEquipment} colId="unassignedNotification" items={filteredData.unassignedNotification} expanded={expanded['unassignedNotification']} onToggle={(e, isExp) => toggleAccordion('unassignedNotification', isExp)} activeId={activeId} />
                </div>
                <Grid container spacing={2}>
                  {Object.entries(filteredData)
                    .filter(([key]) => key !== 'unassignedNotification')
                    .map(([colId, items]) => (
                      <Grid item xs={colSize} key={colId}>
                        <Column handleUserClick={handleUserClick} handleClickFromUnassignedMenuList={handleClickPromAssignment} unassignedNotification={filteredData.unassignedNotification} isFocused={isFocused} showEquipment={showEquipment} colId={colId} items={items} expanded={expanded[colId]} onToggle={(e, isExp) => toggleAccordion(colId, isExp)} activeId={activeId} />
                      </Grid>
                    ))}
                </Grid>
              </div>
              <DragOverlay>
                {activeId ? <SortableItem id={activeId} item={targetNotification} showEquipment={showEquipment} /> : null}
              </DragOverlay>
            </DndContext>

            <br />
            {/* <Divider orientation='horizontal' />
            <br /> */}

            {/* <Typography component={"h1"}><b>API Request :</b></Typography>
            <pre
              style={{
                background: '#0f172a',
                color: '#f8fafc',
                padding: '1rem',
                borderRadius: '8px',
                overflowX: 'auto',
                fontSize: '0.85rem',
              }}
            >
              {JSON.stringify(backendOutputObj, null, 2)}
            </pre> */}
          </div> : ""
      }
    </>
  );
})